/** @license @dairyfarm/eval-web v3.0.0
* Copyright 2020 Texas A&M Center for Applied Technology, 
* Texas A&M Engineering Experiment Station, The Texas A&M 
* University System.
* 
* This source code/schema and any derivative work is Texas 
* A&M Engineering Experiment Station intellectual property 
* and protected by copyright law. Usage is restricted solely 
* to the FARM Animal Care and Environmental Stewardship 
* programs administered by the National Milk Producers 
* Federation. Dissemination of this source code and/or 
* derivative work creation is strictly prohibited without 
* express written permission from the Texas A&M Engineering 
* Experiment Station.
*/
import { combineReducers } from 'redux';
import React from 'react';
import { getType } from 'typesafe-actions';
import { AppAction } from '..';
import { beginPublish, cancelPublish, cleanupActions, doPublishActions, publishPromptActions } from '../evaluations';
import { networkDownForPublish } from './../evaluations/actions';
import { acknowledgePublishError, acknowledgePublishSuccess } from './actions';

export interface SubmitState {
  submitting: boolean;
  error?: string | React.ReactNode;
  step?: 'network-test' | 'cleanup' | 'prompts' | 'submitting' | 'submitted';
}

export interface InterfaceState {
  submitState: SubmitState;
}

function submitStateReducer(
  state: SubmitState = {
    submitting: false,
  },
  action: AppAction
): SubmitState {
  switch (action.type) {
    case getType(beginPublish):
      return {
        submitting: true,
        step: 'network-test',
      };
    case getType(networkDownForPublish):
      return {
        ...state,
        error: 'Network not available. Please try again when online',
      };
    case getType(cleanupActions.request):
      return {
        submitting: true,
        step: 'cleanup',
      };
    case getType(publishPromptActions.request):
      return {
        submitting: true,
        step: 'prompts',
      };
    case getType(doPublishActions.request):
      return {
        submitting: true,
        step: 'submitting',
      };
    case getType(doPublishActions.success):
      return {
        submitting: true,
        step: 'submitted',
      };
    case getType(doPublishActions.failure):
      // TODO: Determine appropriate message to display
      let message: string;
      switch (action.payload.status) {
        case 400:
        case 409:
        case 500:
          message = 'Something went wrong. Please contact technical support for assistance.';
          break;
        case 403:
          message = 'Only certified evaluators are allowed to publish an evaluation.';
          break;
        case 404:
          message = 'The server is temporarily unavailable. Please try again later';
          break;
        default:
          message = 'Something went wrong when submitting. Please try again later.';
          break;
      }

      return {
        ...state,
        error: message,
      };
    case getType(cancelPublish):
    case getType(acknowledgePublishError):
    case getType(acknowledgePublishSuccess):
      return {
        submitting: false,
      };

    default:
      return state;
  }
}

export const interfaceReducer = combineReducers({
  submitState: submitStateReducer,
});
